import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { 
  Globe, 
  Clock,
  Trash2,
  RefreshCw,
  Loader2,
  ChevronDown,
  ArrowRight,
  Download,
  AlertTriangle
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterLang, setFilterLang] = useState(null);
  const [lowConfidenceOnly, setLowConfidenceOnly] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, [filterLang, lowConfidenceOnly]);

  const fetchHistory = async () => {
    try {
      setRefreshing(true);
      const params = new URLSearchParams();
      params.append("limit", "100");
      if (filterLang) {
        params.append("source_lang", filterLang);
      }
      if (lowConfidenceOnly) {
        params.append("low_confidence_only", "true");
      }
      
      const response = await axios.get(`${API}/history?${params.toString()}`);
      setHistory(response.data.translations || []);
    } catch (error) {
      console.error("Failed to fetch history:", error);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const exportHistory = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      params.append("format", "csv");
      if (filterLang) {
        params.append("source_lang", filterLang);
      }
      
      const response = await axios.get(`${API}/history/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `translations_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded successfully");
    } catch (error) {
      console.error("Failed to export history:", error);
      toast.error("Failed to export history");
    } finally {
      setExporting(false);
    }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/history`);
      setHistory([]);
      toast.success("History cleared");
    } catch (error) {
      console.error("Failed to clear history:", error);
      toast.error("Failed to clear history");
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.9) return "text-emerald-600";
    if (score >= 0.75) return "text-primary";
    if (score >= 0.6) return "text-secondary";
    return "text-red-500";
  };

  return (
    <div className="min-h-screen pb-12 bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Globe className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">MeteoTranslate</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/translate" className="nav-link text-sm font-medium" data-testid="nav-translate">
              Translate
            </Link>
            <Link to="/terminology" className="nav-link text-sm font-medium" data-testid="nav-terminology">
              Terminology
            </Link>
            <Link to="/metrics" className="nav-link text-sm font-medium" data-testid="nav-metrics">
              Metrics
            </Link>
            <Link to="/analytics" className="nav-link text-sm font-medium" data-testid="nav-analytics">
              Analytics
            </Link>
            <Link to="/history" className="nav-link active text-sm font-medium" data-testid="nav-history">
              History
            </Link>
            <Link to="/settings" className="nav-link text-sm font-medium" data-testid="nav-settings">
              Settings
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-primary" />
              <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground" data-testid="page-title">
                Translation History
              </h1>
            </div>
            <p className="text-muted-foreground">
              Audit trail of all translations with timestamps and confidence scores
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Low Confidence Filter */}
            <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
              <Switch
                id="low-confidence"
                checked={lowConfidenceOnly}
                onCheckedChange={setLowConfidenceOnly}
                data-testid="low-confidence-toggle"
              />
              <Label htmlFor="low-confidence" className="text-sm flex items-center gap-1 cursor-pointer">
                <AlertTriangle className="w-4 h-4 text-secondary" />
                Flagged Only
              </Label>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-border" data-testid="filter-dropdown">
                  {filterLang ? filterLang.toUpperCase() : "All Languages"}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border-border">
                <DropdownMenuItem onClick={() => setFilterLang(null)}>
                  All Languages
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilterLang("en-ca")}>
                  EN-CA (English)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFilterLang("fr-ca")}>
                  FR-CA (French)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              onClick={exportHistory}
              disabled={exporting || history.length === 0}
              className="border-border"
              data-testid="export-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span className="ml-2 hidden sm:inline">Export CSV</span>
            </Button>

            <Button
              variant="outline"
              onClick={fetchHistory}
              disabled={refreshing}
              className="border-border"
              data-testid="refresh-btn"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="border-border text-destructive hover:text-destructive" data-testid="clear-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white border-border">
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear Translation History?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all translation records. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-muted border-border">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={clearHistory} className="bg-destructive text-white">
                    Clear History
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* History Table */}
        <div className="bg-white rounded-lg border border-border overflow-hidden shadow-card">
          <ScrollArea className="h-[600px]">
            <table className="data-table">
              <thead className="sticky top-0">
                <tr>
                  <th className="w-40">Timestamp</th>
                  <th>Source Text</th>
                  <th className="w-24 text-center">Direction</th>
                  <th>Translated Text</th>
                  <th className="w-24 text-center">Confidence</th>
                  <th className="w-20 text-center">Time</th>
                  <th className="w-40">Model</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      <td><div className="skeleton h-4 w-32 rounded" /></td>
                      <td><div className="skeleton h-4 w-48 rounded" /></td>
                      <td><div className="skeleton h-4 w-16 rounded mx-auto" /></td>
                      <td><div className="skeleton h-4 w-48 rounded" /></td>
                      <td><div className="skeleton h-4 w-12 rounded mx-auto" /></td>
                      <td><div className="skeleton h-4 w-12 rounded mx-auto" /></td>
                      <td><div className="skeleton h-4 w-24 rounded" /></td>
                    </tr>
                  ))
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-muted-foreground">
                      {lowConfidenceOnly 
                        ? "No low-confidence translations found."
                        : "No translation history yet. Start translating to build your audit trail."}
                    </td>
                  </tr>
                ) : (
                  history.map((item, i) => (
                    <tr 
                      key={item.id || i} 
                      className={`table-row-hover ${item.confidence_alert ? 'bg-red-50' : ''}`}
                      data-testid={`history-row-${i}`}
                    >
                      <td className="font-mono text-xs text-muted-foreground">
                        {formatDate(item.timestamp)}
                      </td>
                      <td>
                        <div className="max-w-[200px] truncate" title={item.source_text}>
                          {item.source_text}
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="inline-flex items-center gap-1 text-xs">
                          <span className="font-medium">{item.source_lang?.toUpperCase()}</span>
                          <ArrowRight className="w-3 h-3 text-muted-foreground" />
                          <span className="font-medium">{item.target_lang?.toUpperCase()}</span>
                        </span>
                      </td>
                      <td>
                        <div className="max-w-[200px] truncate" title={item.translated_text}>
                          {item.translated_text}
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {item.confidence_alert && (
                            <AlertTriangle className="w-3 h-3 text-secondary" />
                          )}
                          <span className={`font-mono text-sm font-bold ${getConfidenceColor(item.confidence_score)}`}>
                            {(item.confidence_score * 100).toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="font-mono text-xs text-muted-foreground">
                          {item.processing_time_ms}ms
                        </span>
                      </td>
                      <td>
                        <span className="font-mono text-xs text-cyan-600 truncate block max-w-[120px]" title={item.model_used}>
                          {item.model_used?.split("/")[1] || item.model_used}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ScrollArea>
        </div>

        {/* Summary Stats */}
        {history.length > 0 && (
          <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">Total Records:</span>{" "}
                <span className="font-mono font-bold">{history.length}</span>
              </div>
              <div>
                <span className="text-muted-foreground">EN→FR:</span>{" "}
                <span className="font-mono font-bold">
                  {history.filter(h => h.source_lang === "en-ca").length}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">FR→EN:</span>{" "}
                <span className="font-mono font-bold">
                  {history.filter(h => h.source_lang === "fr-ca").length}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Flagged:</span>{" "}
                <span className="font-mono font-bold text-secondary">
                  {history.filter(h => h.confidence_alert).length}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Avg. Confidence:</span>{" "}
                <span className="font-mono font-bold text-emerald-600">
                  {history.length > 0 
                    ? (history.reduce((sum, h) => sum + h.confidence_score, 0) / history.length * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
